import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import {
    Badge,
    Button,
    Card,
    Col,
    Form,
    Modal,
    Pagination,
    Row,
    Spinner,
    Table,
    InputGroup,
    FormControl,
    FormSelect
} from 'react-bootstrap';
import DatePicker from "react-datepicker";
import Select from 'react-select';
import makeAnimated from "react-select/animated";
import { toast } from 'react-toastify';
import { Search, Printer, ChevronDown, ChevronUp, FileEarmarkSpreadsheet, ArrowLeft } from 'react-bootstrap-icons';
import Header from '../../layouts/Header';
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from 'react-router-dom';

const UserAnalyticsReport = () => {

    const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem("authToken");
    const animatedComponents = makeAnimated();
    const navigate = useNavigate();

    // State variables
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedOutlet, setSelectedOutlet] = useState('');
    const [selectedOutletName, setSelectedOutletName] = useState('');
    const [outlets, setOutlets] = useState([]);
    const [minPrice, setMinPrice] = useState("");
    const [maxPrice, setMaxPrice] = useState("");
    const [error, setError] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('');
    const [productId, setProductId] = useState('');
    const [productsList, setProductsList] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(25);
    const [selectedTaste, setSelectedTaste] = useState('');
    const [totalPages, setTotalPages] = useState(1);
    const [filteredData, setFilteredData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [referredUsers, setReferredUsers] = useState([]);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [singleUserDetails, setSingleUserDetails] = useState({});
    const [searchText, setSearchText] = useState('');
    const [downloadBtnLoading, setDownloadBtnLoading] = useState(false);
    const [showMoreFilters, setShowMoreFilters] = useState(false);
    const [sortType, setSortType] = useState('');
    const [sortBy, setSortBy] = useState('');
    const [loading, setLoading] = useState(false);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: "100%",
            borderRadius: "6px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : null,
            "&:hover": {
                borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            },
            backgroundColor: skin === 'dark' ? '#343a40' : '',
            color: skin === 'dark' ? '#fff' : ''
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: skin === 'dark' ? '#343a40' : '',
            color: skin === 'dark' ? '#fff' : ''
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? (skin === 'dark' ? '#495057' : '#e9ecef') : '',
            color: skin === 'dark' ? '#fff' : '#212529'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: skin === 'dark' ? '#fff' : ''
        }),
        placeholder: (provided) => ({
            ...provided,
            color: "#6c757d",
        }),
        input: (provided) => ({
            ...provided,
            color: skin === 'dark' ? '#fff' : ''
        })
    };

    // Fetch data functions
    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("outlet", { headers });
            if (response?.data?.status === true) {
                const activeOutlets = response?.data?.data?.outlets.filter(outlet => outlet?.status === "ACTIVE" || "INACTIVE");
                setOutlets(activeOutlets);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch outlets");
        }
    };

    const getCategoryList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("masters/category/get", { headers });
            if (response?.data?.status === true) {
                setCategoryList(response?.data?.data?.categories);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch categories");
        }
    };

    const getProductsList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axios.get("product", { headers });
            if (response?.data?.status === true) {
                setProductsList(response?.data?.data?.products);
                setFilteredProducts(response?.data?.data?.products);
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch products");
        }
    };

    const getUserAnalyticsReport = async (sDate, eDate, page, limit, min, max, filter, taste, outlet, category, product, search, download, sortTy, sortB) => {
        setLoading(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                "content-type": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            };

            const updatedStartDate = sDate ? sDate.toISOString().split("T")[0] : null;
            const updatedEndDate = eDate ? eDate.toISOString().split("T")[0] : null;

            // let apiUrl = `user/user-analytics?page=${page}&limit=${limit}&startDate=${updatedStartDate}&endDate=${updatedEndDate}`; Old Api
            let apiUrl = `user/user-analytics/new?page=${page}&limit=${limit}&startDate=${updatedStartDate}&endDate=${updatedEndDate}`;

            const queryParams = {
                minPrice: min,
                maxPrice: max,
                filter,
                foodType: taste,
                outlet,
                category,
                product,
                search,
                isDownload: download,
                sortType: sortTy,
                sortBy: sortB
            };

            Object.entries(queryParams).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    apiUrl += `&${key}=${value}`;
                }
            });

            if (download) {
                const response = await axios.get(apiUrl, { headers, responseType: 'blob' });
                const blob = new Blob([response.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });

                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${selectedOutletName} User Analytics Report (${updatedStartDate} - ${updatedEndDate}).xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);

                toast.success('Excel file downloaded successfully!', {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            } else {
                const response = await axios.get(apiUrl, { headers });
                setFilteredData(response?.data?.data?.userAnalytics || []);
                setTotalRecords(response?.data?.data?.pagination?.totalRecords || 0);
                setTotalPages(response?.data?.data?.pagination?.totalPages || 1);

                toast.success(response?.data?.data?.message || "Data loaded successfully", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light"
                });
            }
        } catch (error) {
            handleApiError(error, "Failed to fetch user analytics");
        } finally {
            setLoading(false);
            setDownloadBtnLoading(false);
        }
    };

    const handleApiError = (error, defaultMessage) => {
        let errorMessage = defaultMessage;
        if (error?.response) {
            if (error.response?.status === 406) {
                errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
            } else if (error.response?.status === 412 || error.response?.status === 404) {
                errorMessage = error?.response?.data?.data?.message;
            } else if (error.response?.status === 500) {
                errorMessage = error?.response?.data?.error;
            }
        }
        toast.error(errorMessage, {
            position: "top-right",
            autoClose: 3000,
            theme: "light",
        });
    };

    // Event handlers
    const handlePageChange = (page) => {
        if (page > 0 && page <= totalPages) {
            setCurrentPage(page);
            const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
            const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
            getUserAnalyticsReport(
                adjustedStartDate, adjustedEndDate, page, recordsPerPage,
                minPrice, maxPrice, selectedFilter, selectedTaste, selectedOutlet,
                selectedCategory, productId, searchText, false, sortType, sortBy
            );
        }
    };

    const handleRecordsPerPageChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setRecordsPerPage(value);
        setCurrentPage(1);
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        getUserAnalyticsReport(
            adjustedStartDate, adjustedEndDate, 1, value,
            minPrice, maxPrice, selectedFilter, selectedTaste, selectedOutlet,
            selectedCategory, productId, searchText, false, sortType, sortBy
        );
    };

    const handleEndDateChange = (date) => {
        if (date && startDate && date < startDate) {
            setError('End date cannot be earlier than start date');
        } else {
            setError('');
            setEndDate(date);
        }
    };

    const handleSubmit = async () => {
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        setCurrentPage(1);
        await getUserAnalyticsReport(
            adjustedStartDate, adjustedEndDate, 1, recordsPerPage,
            minPrice, maxPrice, selectedFilter, selectedTaste, selectedOutlet,
            selectedCategory, productId, searchText, false, sortType, sortBy
        );
    };

    const handleDownloadExcel = async () => {
        setDownloadBtnLoading(true);
        const adjustedStartDate = new Date(startDate.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate.setHours(23, 59, 59, 999));
        await getUserAnalyticsReport(
            adjustedStartDate, adjustedEndDate, 1, recordsPerPage,
            minPrice, maxPrice, selectedFilter, selectedTaste, selectedOutlet,
            selectedCategory, productId, searchText, true, sortType, sortBy
        );
    };

    const handleOpenViewModal = async (data) => {
        await getSingleUserReferralUsers(data?._id);
        setOpenViewModal(true);
        // setReferredUsers(data?.referredUsers || []);
        // setSingleUserDetails(data || {});
        console.log("Data :", data?._id);

    };

    const handleCloseViewModal = () => {
        setOpenViewModal(false);
    };

    const getSingleUserReferralUsers = async (userId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            }
            const response = await axios.get(`user/referral-users-orders/${userId}`, { headers });
            console.log("Response of refferral Users :", response);
            setReferredUsers(response?.data?.data?.referralUsers || [])
        } catch (error) {
            console.log("error :", error);
        }
    }

    // Initialize data
    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));
        setStartDate(start);
        setEndDate(end);

        getOutlets();
        getCategoryList();
        getProductsList();
    }, []);

    // Disable certain filters when specific options are selected
    useEffect(() => {
        const disabledFilters = [
            'JOINING_COUPON_ONE_ORDER',
            'COUPON_ORDERS',
            'NO_COUPON_ORDERS',
            'REFERRING_USERS',
            'REFERRED_USERS',
        ];

        if (disabledFilters.includes(selectedFilter)) {
            setMinPrice("");
            setMaxPrice("");
        }
    }, [selectedFilter]);

    // Prepare category options for Select component
    const categoryOptions = categoryList.map((category) => ({
        value: category._id,
        label: category.name,
    }));

    // Render pagination items
    const renderPaginationItems = () => {
        const items = [];
        const maxVisiblePages = 5;
        let startPage, endPage;

        if (totalPages <= maxVisiblePages) {
            startPage = 1;
            endPage = totalPages;
        } else {
            const maxVisibleBeforeCurrent = Math.floor(maxVisiblePages / 2);
            const maxVisibleAfterCurrent = Math.ceil(maxVisiblePages / 2) - 1;

            if (currentPage <= maxVisibleBeforeCurrent) {
                startPage = 1;
                endPage = maxVisiblePages;
            } else if (currentPage + maxVisibleAfterCurrent >= totalPages) {
                startPage = totalPages - maxVisiblePages + 1;
                endPage = totalPages;
            } else {
                startPage = currentPage - maxVisibleBeforeCurrent;
                endPage = currentPage + maxVisibleAfterCurrent;
            }
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div
                className={`main main-app p-3 p-lg-4 ${skin === "dark" ? "bg-dark text-light" : "bg-light"
                    }`}
            >
                <Card className={`mb-4 ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}>

                    <div className="card-header bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-0 fw-bold d-inline-block">
                                <i className="bi bi-people-fill me-2"></i>USER ANALYTICS REPORT
                            </h4>
                        </div>
                        <div>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => navigate('/reports/all-reports')}
                                className="me-3 d-flex align-items-center fw-semibold"
                                style={{ height: '38px' }}
                            >
                                <ArrowLeft className="me-1" /> Back to Reports
                            </Button>
                        </div>
                    </div>
                    <hr className='mt-0 mb-0' />

                    <Card.Body>
                        {/* Filters Section */}
                        <div className="mb-4 p-3 bg-light rounded-3">
                            <Row className="g-3">
                                <Col md={2}>
                                    <Form.Group controlId="outletSelect">
                                        <Form.Label className="fw-semibold">
                                            <i className="bi bi-shop me-2"></i>Outlet
                                        </Form.Label>
                                        <Form.Select
                                            value={selectedOutlet}
                                            onChange={(e) => {
                                                setSelectedOutlet(e.target.value)
                                                console.log("Selected Outlet Name :", e.target.selectedOptions);

                                                setSelectedOutletName(e.target.selectedOptions[0].label)
                                            }
                                            }
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value="">All Outlets</option>
                                            {outlets?.map((outlet) => (
                                                <option key={outlet?._id} value={outlet?._id} label={`${outlet?.name} (${outlet?.city})`}>
                                                    {outlet?.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                {/* <Col md={2}>
                                <Form.Group controlId="startDate">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-calendar me-2"></i>Start Date
                                    </Form.Label>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(date)}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="Start Date"
                                        className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={2}>
                                <Form.Group controlId="endDate">
                                    <Form.Label className="fw-semibold">
                                        <i className="bi bi-calendar me-2"></i>End Date
                                    </Form.Label>
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="End Date"
                                        minDate={startDate}
                                        className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                    />
                                    {error && <div className="text-danger small mt-1">{error}</div>}
                                </Form.Group>
                            </Col> */}
                                <Col md={2}>
                                    <Form.Group controlId="startDate">
                                        <Form.Label className="fw-semibold">
                                            <i className="bi bi-calendar me-2"></i>Start Date
                                        </Form.Label>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => {
                                                setStartDate(date);
                                                // Reset endDate if it's outside the new 60-day window
                                                if (endDate && date && endDate > new Date(date.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                    setEndDate(null);
                                                }
                                            }}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="Start Date"
                                            className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="endDate">
                                        <Form.Label className="fw-semibold">
                                            <i className="bi bi-calendar me-2"></i>End Date
                                        </Form.Label>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={(date) => {
                                                if (startDate && date > new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000)) {
                                                    setError("End date cannot be more than 60 days after start date");
                                                } else {
                                                    setError("");
                                                    handleEndDateChange(date);
                                                }
                                            }}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="End Date"
                                            minDate={startDate}
                                            maxDate={startDate ? new Date(startDate.getTime() + 60 * 24 * 60 * 60 * 1000) : null}
                                            className={`form-control ${skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}`}
                                        />
                                        {error && <div className="text-danger small mt-1">{error}</div>}
                                    </Form.Group>
                                </Col>


                                <Col md={2}>
                                    <Form.Group controlId="minPrice">
                                        <Form.Label className="fw-semibold">
                                            Min Price
                                        </Form.Label>
                                        <FormControl
                                            type="number"
                                            min={0}
                                            value={minPrice}
                                            onChange={(e) => setMinPrice(e.target.value)}
                                            placeholder="Enter Min Price"
                                            disabled={['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'].includes(selectedFilter)}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="maxPrice">
                                        <Form.Label className="fw-semibold">
                                            Max Price
                                        </Form.Label>
                                        <FormControl
                                            type="number"
                                            min={0}
                                            value={maxPrice}
                                            onChange={(e) => setMaxPrice(e.target.value)}
                                            placeholder="Enter Max Price"
                                            disabled={['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'].includes(selectedFilter)}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="userType">
                                        <Form.Label className="fw-semibold">
                                            User Type
                                        </Form.Label>
                                        <Form.Select
                                            value={selectedFilter}
                                            onChange={(e) => setSelectedFilter(e.target.value)}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value="">Select</option>
                                            <option value="JOINING_COUPON_ONE_ORDER">Joining Coupon Users (1 order)</option>
                                            <option value="COUPON_ORDERS">Coupon Users</option>
                                            <option value="NO_COUPON_ORDERS">Non-Coupon Users</option>
                                            <option value="REFERRING_USERS">Referring Users</option>
                                            <option value="REFERRED_USERS">Referred Users</option>
                                            <option value="REPEATED_USERS_DAYS">Repeated Users (By Days)</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="g-3 mt-2">
                                <Col md={2}>
                                    <Form.Group controlId="categorySelect">
                                        <Form.Label className="fw-semibold">
                                            Category
                                        </Form.Label>
                                        <Select
                                            options={categoryOptions}
                                            value={categoryOptions.find(option => option.value === selectedCategory)}
                                            onChange={(selectedOption) => {
                                                const selectCategory = selectedOption?.value;
                                                setSelectedCategory(selectCategory);
                                                if (!selectCategory) {
                                                    setFilteredProducts(productsList);
                                                } else {
                                                    setFilteredProducts(productsList.filter(product => product?.category?._id === selectCategory));
                                                }
                                            }}
                                            isClearable
                                            isDisabled={['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'].includes(selectedFilter)}
                                            styles={customStyles}
                                            placeholder="Category"
                                            theme={(theme) => ({
                                                ...theme,
                                                borderRadius: 0,
                                                colors: {
                                                    ...theme.colors,
                                                    primary25: skin === 'dark' ? '#495057' : '#e9ecef',
                                                    primary: '#007bff',
                                                },
                                            })}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="productSelect">
                                        <Form.Label className="fw-semibold">
                                            Product
                                        </Form.Label>
                                        <Select
                                            options={filteredProducts?.map(product => ({
                                                value: product._id,
                                                label: product.name
                                            }))}
                                            value={productId ? {
                                                value: productId,
                                                label: filteredProducts.find(p => p._id === productId)?.name
                                            } : null}
                                            onChange={(selectedOption) => {
                                                setProductId(selectedOption ? selectedOption.value : '');
                                            }}
                                            isDisabled={['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'].includes(selectedFilter)}
                                            isClearable
                                            styles={customStyles}
                                            placeholder="Product"
                                        />
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="tasteSelect">
                                        <Form.Label className="fw-semibold">
                                            Preferred Taste
                                        </Form.Label>
                                        <Form.Select
                                            value={selectedTaste}
                                            onChange={(e) => setSelectedTaste(e.target.value)}
                                            disabled={['JOINING_COUPON_ONE_ORDER', 'COUPON_ORDERS', 'NO_COUPON_ORDERS', 'REFERRING_USERS', 'REFERRED_USERS'].includes(selectedFilter)}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value="">Select</option>
                                            <option value="FOOD">Food</option>
                                            <option value="BEVERAGE">Beverage</option>
                                            <option value="BOTH">Both</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="sortColumn">
                                        <Form.Label className="fw-semibold">
                                            Sort Column
                                        </Form.Label>
                                        <Form.Select
                                            value={sortType}
                                            onChange={(e) => setSortType(e.target.value)}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value="">Select Column</option>
                                            <option value="TOTAL_VISITS">Total Visits</option>
                                            <option value="AVG_TOTAL_SPENDINGS">Avg. Spendings</option>
                                            <option value="TOTAL_SPENT">Total Spent</option>
                                            <option value="TOTAL_FOOD_COUNT">Food Count</option>
                                            <option value="TOTAL_BEVERAGE_COUNT">Beverage Count</option>
                                            <option value="TOTAL_ORDER_COUNT">Order Count</option>
                                            <option value="REFERRAL_COUNT">Referral Count</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2}>
                                    <Form.Group controlId="sortOrder">
                                        <Form.Label className="fw-semibold">
                                            Sort Order
                                        </Form.Label>
                                        <Form.Select
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            disabled={!sortType}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value="">Select Order</option>
                                            <option value="ASC">Ascending</option>
                                            <option value="DESC">Descending</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col md={2} className="d-flex align-items-end">
                                    <Button
                                        variant="primary"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        className="w-100"
                                    >
                                        {loading ? (
                                            <Spinner as="span" animation="border" size="sm" />
                                        ) : "Submit"}
                                    </Button>
                                </Col>
                            </Row>

                            <Row className="g-3 mt-2 align-items-center">
                                <Col md={2}>
                                    <div
                                        onClick={() => setShowMoreFilters(!showMoreFilters)}
                                        style={{ cursor: 'pointer', color: '#0d6efd', fontWeight: '500', display: 'inline-flex', alignItems: 'center' }}
                                    >
                                        {showMoreFilters ? 'Hide Search Filter' : 'Show Search Filter'}
                                        {showMoreFilters ? <ChevronUp className="ms-2" /> : <ChevronDown className="ms-2" />}
                                    </div>
                                </Col>

                                {showMoreFilters && (
                                    <Col md={4}>
                                        <Form.Group controlId="searchInput">
                                            <InputGroup>
                                                <FormControl
                                                    type="text"
                                                    placeholder="Search by name or mobile"
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                                />
                                                <Button
                                                    variant={skin === 'dark' ? 'secondary' : 'outline-primary'}
                                                    onClick={handleSubmit}
                                                    disabled={loading}
                                                >
                                                    <Search className="me-1" />
                                                    Search
                                                </Button>
                                            </InputGroup>
                                        </Form.Group>
                                    </Col>
                                )}
                            </Row>
                        </div>

                        {/* Results Section */}
                        {filteredData.length === 0 && !loading ? (
                            <div className="d-flex justify-content-center align-items-center py-5 my-5 bg-light rounded-3">
                                <div className="text-center">
                                    <i className="bi bi-people fs-1 text-muted"></i>
                                    <h5 className="text-muted mt-3">
                                        {selectedOutlet ? "No users found for selected filters" : "Please select filters and click Submit"}
                                    </h5>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Actions and Pagination - Top */}
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <Button
                                        variant="outline-success"
                                        className="me-2 fw-semibold w-25"
                                        onClick={handleDownloadExcel}
                                        disabled={filteredData.length === 0 || downloadBtnLoading}
                                    >
                                        {downloadBtnLoading ? (
                                            <Spinner as="span" animation="border" size="sm" />
                                        ) : (
                                            <>
                                                <FileEarmarkSpreadsheet className="me-2" />
                                                Download Excel
                                            </>
                                        )}
                                    </Button>

                                    <div className="d-flex align-items-center">
                                        <span className="me-2 text-muted">Show</span>
                                        <Form.Select
                                            style={{ width: 'auto' }}
                                            value={recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                            className={skin === 'dark' ? 'bg-dark text-light border-secondary' : ''}
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                            <option value={500}>500</option>
                                            <option value={1000}>1000</option>
                                        </Form.Select>
                                        <span className="ms-2 text-muted">entries</span>
                                    </div>
                                </div>

                                {/* Users Table */}
                                <div className="table-responsive border rounded-3">
                                    <Table striped bordered hover responsive className={skin === 'dark' ? 'dark-table' : ''}>
                                        <thead className={skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}>
                                            <tr>
                                                <th width="50">#</th>
                                                <th>User Details</th>
                                                <th>Outlet</th>
                                                <th>Total Visits</th>
                                                <th>Avg. Spendings</th>
                                                <th>Total Spent</th>
                                                <th>Food Count</th>
                                                <th>Beverage Count</th>
                                                {filteredData.some(user => user?.orderStatistics?.totalCategoriesCount > 0) && (
                                                    <th style={{ color: 'purple' }}>Category Count</th>
                                                )}
                                                {filteredData.some(user => user?.orderStatistics?.totalProductsCount > 0) && (
                                                    <th style={{ color: 'purple' }}>Product Count</th>
                                                )}
                                                <th>Order Count</th>
                                                <th>Referral Count</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={12 + (filteredData.some(u => u?.orderStatistics?.totalCategoriesCount > 0) ? 1 : 0) + (filteredData.some(u => u?.orderStatistics?.totalProductsCount > 0) ? 1 : 0)} className="text-center py-4">
                                                        <Spinner animation="border" variant="primary" />
                                                        <p className="mt-2 mb-0">Loading user data...</p>
                                                    </td>
                                                </tr>
                                            ) : filteredData.length > 0 ? (
                                                filteredData.map((user, index) => (
                                                    <tr key={user._id}>
                                                        <td className="text-muted">
                                                            {(currentPage - 1) * recordsPerPage + index + 1}
                                                        </td>
                                                        <td>
                                                            <div className="fw-semibold" style={{ color: 'purple' }}>{user.name}</div>
                                                            <div className="text-primary">{user.email}</div>
                                                            <div className="text-primary">{user.mobile}</div>
                                                            <div className="text-muted small">
                                                                {new Date(user.createdAt).toLocaleString("en-IN", {
                                                                    day: "2-digit",
                                                                    month: "2-digit",
                                                                    year: "numeric",
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                    hour12: true
                                                                })}
                                                            </div>
                                                        </td>
                                                        <td>{user?.orderOutlet?.name || "-------"}</td>
                                                        <td className="text-center">{user?.orderDates?.length || 0}</td>
                                                        <td className="text-end">{user?.orderStatistics?.averageGrandTotal ? `₹ ${user?.orderStatistics?.averageGrandTotal?.toFixed(2)}` : '-------'}</td>
                                                        <td className="text-end">{user?.orderStatistics?.totalGrandTotal ? `₹ ${user?.orderStatistics?.totalGrandTotal?.toFixed(2)}` : '-------'}</td>
                                                        <td className="text-center">{user?.orderStatistics?.totalFoodCount || 0}</td>
                                                        <td className="text-center">{user?.orderStatistics?.totalBeverageCount || 0}</td>
                                                        {filteredData.some(u => u?.orderStatistics?.totalCategoriesCount > 0) && (
                                                            <td className="text-center">{user?.orderStatistics?.totalCategoriesCount || 0}</td>
                                                        )}
                                                        {filteredData.some(u => u?.orderStatistics?.totalProductsCount > 0) && (
                                                            <td className="text-center">{user?.orderStatistics?.totalProductsCount || 0}</td>
                                                        )}
                                                        <td className="text-center">{user?.orderStatistics?.totalOrderCount || 0}</td>
                                                        <td className="text-center">{user?.referredUsers?.length || 0}</td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleOpenViewModal(user)}
                                                                disabled={!user?.referredUsers?.length}
                                                            >
                                                                View Referrals
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={12 + (filteredData.some(u => u?.orderStatistics?.totalCategoriesCount > 0) ? 1 : 0) + (filteredData.some(u => u?.orderStatistics?.totalProductsCount > 0) ? 1 : 0)} className="text-center py-4">
                                                        <i className="bi bi-exclamation-circle fs-4 text-muted"></i>
                                                        <p className="mt-2 mb-0 text-muted">No users found for selected criteria</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                                {/* Pagination - Bottom */}
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {(currentPage - 1) * recordsPerPage + 1} to{' '}
                                        {Math.min(currentPage * recordsPerPage, filteredData.length)} of{' '}
                                        {filteredData.length} entries
                                    </div>
                                    <Pagination>
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {renderPaginationItems()}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>

                {openViewModal && (
                    <Modal
                        show={openViewModal}
                        onHide={() => setOpenViewModal(false)}
                        size="xl"
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title className="w-100 text-center">
                                <span className='text-muted'>Users Referred By - </span>
                                <span className='text-success'>{singleUserDetails?.name}</span>
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Row>
                                {referredUsers?.length > 0 ? (
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th className="fw-semibold">Created On</th>
                                                <th className="fw-semibold">Name</th>
                                                <th className="fw-semibold">Email</th>
                                                <th className="fw-semibold">Mobile</th>
                                                <th className="fw-semibold">Placed First Order?</th>
                                                <th className="fw-semibold">Status of 1st Order?</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {referredUsers.map((item) => (
                                                <tr key={item._id}>
                                                    <td>
                                                        {new Date(item?.createdAt).toLocaleString("en-IN", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            year: "numeric",
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                            hour12: true,
                                                        })}
                                                    </td>
                                                    <td>
                                                        <span>{item?.name}</span><br />
                                                        <span className="text-muted">{item?.isDeletedBefore ? "[Deleted User]" : ""}</span>
                                                    </td>
                                                    <td>{item?.email}</td>
                                                    <td>{item?.mobile}</td>
                                                    <td>{item?.referrredUsersOrders?.length > 0 ? "YES" : "NOT YET"}</td>
                                                    <td className="text-center">
                                                        <Badge
                                                            bg={
                                                                item?.referrredUsersOrders[0]?.orderStatus === "CONFIRMED"
                                                                    ? "warning"
                                                                    : item?.referrredUsersOrders[0]?.orderStatus === "COMPLETED"
                                                                        ? "primary"
                                                                        : item?.referrredUsersOrders[0]?.orderStatus === "DELIVERED"
                                                                            ? "success"
                                                                            : "secondary"
                                                            }
                                                            className="w-75"
                                                        >
                                                            {item?.referrredUsersOrders[0]?.orderStatus || "N/A"}
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                ) : (
                                    <h6 className="text-danger text-center w-100">
                                        This User Has Not Referred Anyone.
                                    </h6>
                                )}
                            </Row>
                        </Modal.Body>
                    </Modal>
                )}
            </div>
        </React.Fragment>
    )
}

export default UserAnalyticsReport
